/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/* How DR states, capabilities and events read in the portal. */

import {
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CirclePause,
  Clock,
  LoaderCircle,
  ShieldCheck,
  ShieldOff,
  type LucideIcon,
} from "lucide-react";

import { apiErrorMessage, apiErrorStatus } from "@src/sources/CutoverSource";

import type { PillTone } from "@src/components/ui/Lists/ListTable";
import type { DrEvent, DrOperationStatus, DrWorkload } from "@src/@types/Dr";

export type StateInfo = { label: string; tone: PillTone; icon?: LucideIcon; spin?: boolean };

const humanise = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");

const WORKLOAD_STATES: { [state: string]: StateInfo } = {
  DISCOVERED: { label: "Not protected", tone: "neutral", icon: ShieldOff },
  PROTECTING: { label: "Protecting", tone: "info", icon: LoaderCircle, spin: true },
  PROTECTED: { label: "Protected", tone: "success", icon: ShieldCheck },
  SYNCING: { label: "Syncing", tone: "info", icon: LoaderCircle, spin: true },
  RPO_BREACH: { label: "RPO breached", tone: "danger", icon: Clock },
  DEGRADED: { label: "Degraded", tone: "warning", icon: CircleAlert },
  UNPROTECTED: { label: "Unprotected", tone: "neutral", icon: ShieldOff },
};

const REPLICATION_STATES: { [state: string]: StateInfo } = {
  INITIALIZING: { label: "First sync", tone: "info", icon: LoaderCircle, spin: true },
  HEALTHY: { label: "Healthy", tone: "success", icon: CircleCheck },
  SYNCING: { label: "Syncing", tone: "info", icon: LoaderCircle, spin: true },
  DEGRADED: { label: "Degraded", tone: "warning", icon: CircleAlert },
  RPO_BREACH: { label: "RPO breached", tone: "danger", icon: Clock },
  FAILED: { label: "Sync failed", tone: "danger", icon: CircleAlert },
  PAUSED: { label: "Paused", tone: "neutral", icon: CirclePause },
  SOURCE_UNAVAILABLE: { label: "Source unavailable", tone: "danger", icon: CircleAlert },
  TARGET_UNAVAILABLE: { label: "Target unavailable", tone: "danger", icon: CircleAlert },
  REPAIR_REQUIRED: { label: "Needs repair", tone: "danger", icon: CircleAlert },
};

export const workloadStateInfo = (state: string): StateInfo =>
  WORKLOAD_STATES[state] || { label: humanise(state), tone: "info", icon: CircleDashed };

export const replicationStateInfo = (state?: string | null): StateInfo =>
  state ? REPLICATION_STATES[state] || { label: humanise(state), tone: "neutral" } : { label: "None", tone: "neutral" };

/** Whether the workload's last recovery point is older than its RPO (paused or not). */
export const outsideRpo = (w: DrWorkload) =>
  Boolean(w.replication && w.replication.lag_seconds !== null && w.replication.rpo_seconds && !w.replication.rpo_met);

/** Workloads someone should look at: degraded, failing, or outside the RPO (a long pause too). */
export const needsAttention = (w: DrWorkload) =>
  ["DEGRADED", "RPO_BREACH"].includes(w.state) ||
  ["FAILED", "RPO_BREACH", "DEGRADED", "SOURCE_UNAVAILABLE", "TARGET_UNAVAILABLE", "REPAIR_REQUIRED"].includes(
    w.replication?.state || "",
  ) ||
  outsideRpo(w);

/** The workload's status pill: nothing spins while its replication is paused. */
export const workloadDisplayState = (w: DrWorkload): StateInfo => {
  const info = workloadStateInfo(w.state);
  return w.replication?.state === "PAUSED" ? { ...info, spin: false, icon: CirclePause } : info;
};

/** "protection started paused" -> "Protection started paused." */
export const sentence = (text?: string | null) => {
  const t = (text || "").trim();
  if (!t) return "";
  const capitalised = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
};

export const isProtected = (w: DrWorkload) => Boolean(w.replication);

export const OPERATION_STATUS: { [status in DrOperationStatus]: { label: string; tone: PillTone } } = {
  supported: { label: "Supported", tone: "success" },
  partial: { label: "Partial", tone: "warning" },
  untested: { label: "Untested", tone: "info" },
  unsupported: { label: "Not available", tone: "neutral" },
};

/** The pair operations, in the order the portal lists them. */
export const OPERATIONS: [string, string][] = [
  ["replication", "Replication"],
  ["incremental_replication", "Incremental syncs"],
  ["app_consistency_windows", "Application consistency (Windows)"],
  ["app_consistency_linux", "Application consistency (Linux)"],
  ["recovery_points", "Several recovery points"],
  ["point_in_time_recovery", "Recovery from an older point"],
  ["failover", "Failover"],
  ["planned_failover", "Planned failover"],
  ["test_failover", "Test failover"],
  ["test_failover_isolated", "Isolated test failover"],
  ["failback", "Failback"],
  ["reverse_replication", "Reverse replication"],
  ["re_protect", "Re-protection"],
  ["multi_vm_consistency", "Multi-VM consistency"],
  ["network_mapping", "Network mapping"],
  ["ip_mapping", "IP re-addressing"],
  ["dns_mapping", "DNS mapping"],
  ["security_mapping", "Security groups"],
  ["public_ip_mapping", "Public IP"],
  ["health_checks", "Health checks"],
];

export const PLATFORM_NAMES: { [platform: string]: string } = {
  nutanix: "Nutanix",
  aws: "AWS",
  azure: "Azure",
  vmware_vsphere: "VMware",
  "hyper-v": "Hyper-V",
  proxmox: "Proxmox",
  kubevirt: "KubeVirt",
  ibm: "IBM Cloud",
  metal: "Physical",
};

export const platformName = (platform?: string | null) => (platform ? PLATFORM_NAMES[platform] || platform : "?");

/** 900 -> "15 min", 5400 -> "1 h 30 min", 90000 -> "1 d 1 h". */
export const formatSeconds = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined) return "—";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days} d ${hours % 24} h` : `${days} d`;
};

const EVENTS: { [kind: string]: StateInfo } = {
  PROTECTION_STARTED: { label: "Protection started", tone: "brand", icon: ShieldCheck },
  SYNC_COMPLETED: { label: "Sync completed", tone: "success", icon: CircleCheck },
  SYNC_FAILED: { label: "Sync failed", tone: "danger", icon: CircleAlert },
  SYNC_REQUESTED: { label: "Sync requested", tone: "info" },
  REPLICATION_MODE_CHANGED: { label: "Full sync instead of incremental", tone: "warning", icon: CircleAlert },
  CAPABILITY_DEGRADED: { label: "Less consistent than the policy", tone: "warning", icon: CircleAlert },
  RPO_BREACH: { label: "RPO breached", tone: "danger", icon: Clock },
  RPO_RECOVERED: { label: "Back within the RPO", tone: "success", icon: Clock },
  PAUSED: { label: "Paused", tone: "neutral", icon: CirclePause },
  RESUMED: { label: "Resumed", tone: "info" },
  UNPROTECTED: { label: "Unprotected", tone: "neutral", icon: ShieldOff },
  TRANSFER_MISSING: { label: "Transfer deleted", tone: "danger", icon: CircleAlert },
  RESEEDED: { label: "Re-protected from the failback point", tone: "success", icon: ShieldCheck },
  RECOVERY_POINTS_UPDATED: { label: "Recovery points updated", tone: "info", icon: ShieldCheck },
  RESTORE_POINT_FAILED: { label: "Restore point not taken", tone: "warning", icon: CircleAlert },
  GROUP_SYNC_STARTED: { label: "Consistency group sync started", tone: "info", icon: ShieldCheck },
  GROUP_SYNC_FAILED: { label: "Consistency group sync not started", tone: "danger", icon: CircleAlert },
  RPO_AT_RISK: { label: "RPO at risk", tone: "warning", icon: CircleAlert },
  RTO_EXCEEDED: { label: "RTO exceeded", tone: "danger", icon: CircleAlert },
};

/** DR run operations as people say them. */
export const RUN_OPERATION_NAMES: { [operation: string]: string } = {
  VALIDATE: "Validation",
  TEST_FAILOVER: "Test failover",
  TEST_CLEANUP: "Test clean-up",
  PLANNED_FAILOVER: "Planned failover",
  EMERGENCY_FAILOVER: "Emergency failover",
  FAILBACK: "Failback",
  FAILBACK_CONFIRM: "Failback confirmation",
  REPROTECT: "Re-protect",
};

export const eventInfo = (kind: string): StateInfo => EVENTS[kind] || { label: humanise(kind), tone: "neutral" };

/** One line saying what an event's details are. */
export const describeEvent = (event: DrEvent): string => {
  const d = event.detail || {};
  switch (event.kind) {
    case "PROTECTION_STARTED":
      return [
        d.transfer_created ? "SwiftIQ created the transfer" : "Linked to the existing transfer",
        d.paused ? "started paused" : "",
        `policy ${d.policy || "?"} (RPO ${formatSeconds(d.rpo_seconds)})`,
        ...(d.warnings || []),
      ]
        .filter(Boolean)
        .join(" · ");
    case "SYNC_COMPLETED":
      return `Sync #${d.number}${d.duration_seconds !== undefined ? ` took ${formatSeconds(d.duration_seconds)}` : ""}`;
    case "SYNC_FAILED":
      return `Sync #${d.number} ${String(d.status || "").toLowerCase()}${d.error ? `: ${d.error}` : ""}`;
    case "REPLICATION_MODE_CHANGED":
      return (d.disks || []).map((x: any) => `${x.disk_id}: ${x.full_sync_reason}`).join("; ");
    case "CAPABILITY_DEGRADED":
      return `${d.achieved}-consistent instead of ${d.wanted}-consistent${d.detail ? `: ${d.detail}` : ""}`;
    case "RPO_BREACH":
    case "RPO_RECOVERED":
      return `Last recovery point ${formatSeconds(d.lag_seconds)} old (RPO ${formatSeconds(d.rpo_seconds)})`;
    case "PAUSED":
    case "UNPROTECTED":
      return d.reason || "";
    case "SYNC_REQUESTED":
      return d.by ? `By ${d.by}` : "";
    case "RESEEDED":
      return `${(d.disks || []).length} disk(s) moved onto the VM's new disks: the next sync copies only what changed since the failback${d.reason ? ` (${d.reason})` : ""}`;
    case "TRANSFER_MISSING":
      return `Transfer ${d.transfer_id} no longer exists`;
    case "RPO_AT_RISK":
      return `Sync #${d.number} took ${formatSeconds(d.duration_seconds)}: at that pace up to ${formatSeconds(
        d.worst_case_seconds,
      )} of data could be lost, more than the RPO (${formatSeconds(d.rpo_seconds)})`;
    case "RTO_EXCEEDED":
      return `${RUN_OPERATION_NAMES[d.operation] || "A failover"} took ${formatSeconds(d.rto_seconds)} to bring it back, more than the RTO (${formatSeconds(
        d.target_seconds,
      )})`;
    case "RESTORE_POINT_FAILED":
      return `The DR site could not keep a restore point of this sync; the replica and its other points are unchanged${
        d.error ? ` (${String(d.error).split("\n")[0]})` : ""
      }`;
    case "GROUP_SYNC_STARTED":
      return `One snapshot of the group's ${d.members || "?"} VMs${d.taken_at ? ` at ${d.taken_at}` : ""}; this VM's sync copies from it`;
    case "GROUP_SYNC_FAILED":
      return d.error || "";
    case "RECOVERY_POINTS_UPDATED": {
      const parts = [];
      if (d.added) parts.push(`${d.added} new`);
      if (d.expired) parts.push(`${d.expired} no longer kept by the DR site`);
      return `${parts.join(", ")}${d.one_state ? " (the DR site keeps one state: the last sync)" : ""}`;
    }
    default:
      return "";
  }
};

/** The API's message, worded for the person when their role is the reason. */
export const drErrorMessage = (err: any, fallback: string): string =>
  apiErrorStatus(err) === 403
    ? "Your role cannot change disaster recovery: that needs the Admin or DR Operator role."
    : apiErrorMessage(err, fallback);
